import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Play,
  Download,
  BookOpen,
  Users,
  Music,
  Sparkles,
  Check,
  ArrowRight,
  Lock,
} from "lucide-react";
import Piano from "../components/Piano";
import AuthButton from "../components/AuthButton";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { pitchClassesFor, SCALES } from "../lib/music";
import { LIFETIME_PRICE_INR } from "../lib/tier";

const FREE_FEATURES = [
  "Major & all three Minor scales",
  "Major, Minor, Diminished, Augmented chords",
  "Diatonic chord batch export",
  "SVG + PDF export (with watermark)",
  "Audio playback of every scale & chord",
];

const PAID_FEATURES = [
  "All 15 scales — pentatonics, blues, modes, whole-tone, chromatic",
  "All 24 chord qualities — 7ths, 9ths, 11ths, 13ths, sus, add9, 6ths",
  "Interactive Circle of Fifths + progression builder",
  "PDF exports without any watermark",
  "One payment, lifetime access — no subscription",
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, openPaywall } = useAuth();

  const goToApp = () => navigate("/app");

  // A pre-highlighted C Major (Ionian) scale for the hero visual
  const heroPCs = pitchClassesFor("C", SCALES.major.intervals);

  return (
    <div
      className="min-h-screen bg-[#F9FAFB]"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="landing-page"
    >
      {/* NAV */}
      <nav className="border-b-2 border-gray-900 bg-white px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-14 h-10 rounded-md bg-gray-900 flex items-center justify-center p-1.5">
            <svg
              viewBox="0 0 60 40"
              width="44"
              height="30"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="KEYVISION mini piano logo"
            >
              <rect x="1" y="1" width="18" height="38" fill="#FFFFFF" stroke="#111827" strokeWidth="1.5" />
              <rect x="21" y="1" width="18" height="38" fill="#673A80" stroke="#111827" strokeWidth="1.5" />
              <rect x="41" y="1" width="18" height="38" fill="#FFFFFF" stroke="#111827" strokeWidth="1.5" />
              <rect x="13" y="1" width="8" height="24" fill="#111827" />
              <rect x="39" y="1" width="8" height="24" fill="#111827" />
            </svg>
          </div>
          <div>
            <div className="font-semibold tracking-tight text-xl leading-none">
              KEYVISION
            </div>
            <div className="text-[10px] text-gray-500 tracking-widest uppercase mt-1">
              by Piano Calling
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Button
            data-testid="landing-launch-app-nav"
            onClick={goToApp}
            variant="outline"
            className="rounded-xl border-2 border-gray-900 h-10 px-4 font-medium hidden md:inline-flex"
          >
            Launch app
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <AuthButton />
        </div>
      </nav>

      {/* HERO */}
      <section className="px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24 border-b-2 border-gray-900 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-3 flex flex-col gap-6">
            <span className="inline-flex items-center gap-2 self-start text-[11px] font-bold uppercase tracking-widest text-[#673A80] bg-[#673A80]/10 rounded-full px-3 py-1.5">
              <Sparkles className="w-3 h-3" />
              For piano educators & students
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-gray-900"
              data-testid="landing-hero-title"
            >
              Turn every scale, chord & progression into a{" "}
              <span className="text-[#673A80]">piano visual</span> — instantly.
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl">
              KEYVISION helps you communicate music theory the way the piano
              actually looks. Type a scale, pick a chord, hit play — and you get
              a clean, downloadable keyboard diagram with the exact notes lit
              up. Perfect for lesson slides, PDF handouts, and daily practice.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <Button
                data-testid="landing-launch-cta"
                onClick={goToApp}
                className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-14 px-7 text-base font-semibold"
              >
                Launch KEYVISION — it&apos;s free to try
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href="#pricing">
                <Button
                  data-testid="landing-see-pricing"
                  variant="outline"
                  className="rounded-xl border-2 border-gray-900 h-14 px-6 text-base font-medium"
                >
                  See what&apos;s inside
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 pt-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#673A80]" strokeWidth={3} />
                No signup to try
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#673A80]" strokeWidth={3} />
                Works in your browser
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#673A80]" strokeWidth={3} />
                ₹399 lifetime — no subscription
              </span>
            </div>
          </div>

          {/* Hero visual — a real Piano SVG with C Major highlighted */}
          <div className="lg:col-span-2 w-full">
            <div className="rounded-2xl border-2 border-gray-900 bg-white p-4 md:p-6 shadow-[8px_8px_0_0_#111827] rotate-[-1deg]">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                Now showing
              </div>
              <div className="text-xl font-semibold tracking-tight mb-3 text-gray-900">
                C Major (Ionian)
              </div>
              <div className="w-full overflow-hidden">
                <Piano
                  octaves={2}
                  startingPc={0}
                  startingOctaveNumber={4}
                  highlightedPCs={heroPCs}
                  highlightColor="#673A80"
                  backgroundColor="#FFFFFF"
                  showLabels={true}
                />
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Music className="w-3.5 h-3.5" />
                C · D · E · F · G · A · B
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="px-6 md:px-10 py-16 md:py-24 border-b-2 border-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 md:mb-16">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#673A80]">
              Built for two kinds of musicians
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-3 max-w-3xl text-gray-900">
              Music theory finally makes sense — because you can{" "}
              <span className="italic">see</span> it.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Educators card */}
            <div
              className="rounded-2xl border-2 border-gray-900 bg-white p-8 md:p-10 flex flex-col gap-5"
              data-testid="landing-audience-educators"
            >
              <div className="w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold tracking-tight text-gray-900">
                  For educators
                </h3>
                <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                  Teach theory in half the time
                </p>
              </div>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Explaining a scale, mode, or extended chord in text is slow —
                and it loses beginners. With KEYVISION you drop a live keyboard
                diagram into any lesson so students see the exact keys they
                need to play. One click and you have a print-ready PDF for
                your course slides, worksheets, or handouts. No more sketching
                on whiteboards or hunting for the right image.
              </p>
              <ul className="flex flex-col gap-2 text-sm text-gray-800">
                {[
                  "Batch-export every diatonic chord in a key in one click",
                  "Compare multiple scales on one page for side-by-side theory",
                  "Circle of Fifths for teaching progressions & key relationships",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check
                      className="w-4 h-4 text-[#673A80] mt-0.5 flex-shrink-0"
                      strokeWidth={3}
                    />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Students card */}
            <div
              className="rounded-2xl border-2 border-gray-900 bg-[#673A80] text-white p-8 md:p-10 flex flex-col gap-5"
              data-testid="landing-audience-students"
            >
              <div className="w-12 h-12 rounded-full bg-white text-[#673A80] flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold tracking-tight">
                  For students
                </h3>
                <p className="text-xs uppercase tracking-widest text-white/70 mt-1">
                  See it. Hear it. Practice it.
                </p>
              </div>
              <p className="text-sm md:text-base text-white/90 leading-relaxed">
                Stuck on a scale? Confused about a chord? Type it into
                KEYVISION — the exact keys light up on a full keyboard, and
                you can hear it played back at your own tempo. Then download
                a clean PDF and stick it on your music stand. It&apos;s the
                cheat-sheet you wish you had in your first year of lessons.
              </p>
              <ul className="flex flex-col gap-2 text-sm text-white/90">
                {[
                  "Download any scale or chord as a keyboard diagram — take it to your piano",
                  "Slow the tempo down and hear each note in the scale",
                  "Save the progressions your teacher gives you and revisit anytime",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={3} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-6 md:px-10 py-16 md:py-24 border-b-2 border-gray-900 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#673A80]">
              What you get
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-3 max-w-2xl text-gray-900">
              Every visual a piano teacher has ever needed — in one tool.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Music,
                title: "15 scales, 24 chords",
                desc: "From C Major to the diminished-whole-half. Correct enharmonic spellings automatically.",
              },
              {
                icon: Play,
                title: "Audio playback",
                desc: "Hear any scale, chord, or full progression. Adjust tempo. Watch the notes light up in time.",
              },
              {
                icon: Download,
                title: "Batch PDF & SVG export",
                desc: "One click for every diatonic chord. One-column or two-column layouts. Print-ready.",
              },
              {
                icon: Sparkles,
                title: "Circle of Fifths",
                desc: "Colour-coded by diatonic function. Click any wedge to build & play a progression.",
              },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div
                key={i}
                className="rounded-2xl border-2 border-gray-900 bg-[#F9FAFB] p-6 flex flex-col gap-3 hover:bg-white transition-colors"
                data-testid={`landing-feature-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#673A80] text-white flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                  {title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        className="px-6 md:px-10 py-16 md:py-24 border-b-2 border-gray-900"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 text-center md:text-left">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#673A80]">
              Simple, honest pricing
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-3 max-w-2xl text-gray-900">
              Try it free. Unlock the rest for a one-time ₹{LIFETIME_PRICE_INR}.
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-3 max-w-2xl leading-relaxed">
              No subscription. No monthly fee. One payment and it&apos;s yours
              forever — including every future scale, chord, and feature we
              add.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* FREE */}
            <div
              className="rounded-2xl border-2 border-gray-900 bg-white p-8 flex flex-col gap-5"
              data-testid="landing-pricing-free"
            >
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">
                  Try before you buy
                </div>
                <h3 className="text-2xl font-semibold tracking-tight text-gray-900">
                  Free
                </h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-semibold tracking-tight text-gray-900">
                  ₹0
                </span>
                <span className="text-sm text-gray-500">/ forever</span>
              </div>
              <ul className="flex flex-col gap-3">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-800">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gray-900/10 text-gray-900 flex-shrink-0 flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="landing-free-cta"
                onClick={goToApp}
                variant="outline"
                className="rounded-xl border-2 border-gray-900 h-12 text-base font-medium mt-auto"
              >
                Start free
              </Button>
            </div>

            {/* PAID */}
            <div
              className="rounded-2xl border-2 border-gray-900 bg-[#111827] text-white p-8 flex flex-col gap-5 relative overflow-hidden"
              data-testid="landing-pricing-paid"
            >
              <span className="absolute top-6 right-6 text-[10px] font-bold uppercase tracking-widest bg-[#673A80] px-2.5 py-1 rounded-full">
                Most useful
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60 mb-1">
                  Everything unlocked
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">
                  Lifetime
                </h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-semibold tracking-tight">
                  ₹{LIFETIME_PRICE_INR}
                </span>
                <span className="text-sm text-white/50 line-through">₹1499</span>
                <span className="text-xs font-bold uppercase tracking-widest text-[#c8a7dc] ml-1">
                  Launch price
                </span>
              </div>
              <ul className="flex flex-col gap-3">
                {PAID_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#673A80] text-white flex-shrink-0 flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="landing-paid-cta"
                onClick={() => {
                  if (user) {
                    openPaywall();
                  } else {
                    // Sign-in first, then paywall opens naturally from inside the app
                    navigate("/app");
                    setTimeout(() => openPaywall(), 300);
                  }
                }}
                className="rounded-xl bg-[#673A80] hover:bg-[#552d6b] text-white h-12 text-base font-semibold mt-auto"
              >
                <Lock className="w-4 h-4 mr-2" />
                Unlock lifetime — ₹{LIFETIME_PRICE_INR}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CREATOR + FINAL CTA */}
      <section className="px-6 md:px-10 py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center gap-6">
          <div className="w-14 h-14 rounded-full bg-[#673A80] text-white flex items-center justify-center">
            <Music className="w-6 h-6" />
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
            Made by a musician, for musicians.
          </h2>
          <p
            className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl"
            data-testid="landing-creator-credit"
          >
            KEYVISION was created by{" "}
            <span className="font-semibold text-gray-900">Aastha Gupta</span> —
            because piano teachers deserve better than clip-art keyboard
            diagrams, and students deserve to actually see what their fingers
            are supposed to do. Every feature here comes from teaching real
            students in real lessons.
          </p>
          <Button
            data-testid="landing-final-cta"
            onClick={goToApp}
            className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-14 px-8 text-base font-semibold mt-2"
          >
            Open KEYVISION
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t-2 border-gray-900 bg-[#F9FAFB] px-6 md:px-10 py-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-900">KEYVISION</span>
            <span>by Piano Calling · Created by Aastha Gupta</span>
          </div>
          <div className="flex items-center gap-4">
            <span>© {new Date().getFullYear()} Piano Calling</span>
            <Link to="/app" className="hover:text-gray-900 underline underline-offset-2">
              Launch app
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
